import React, { useRef, useState, useEffect } from 'react';
import {
  MAX_ACTIVITY_IMAGES,
  uploadActivityImage,
  uploadWithResizePrompt,
  resizeConfirmOptions,
} from '../../../services/uploads';
import { useConfirm } from '../ConfirmDialog/ConfirmDialog';
import {
  UploaderWrap,
  PreviewGrid,
  PreviewItem,
  CoverBadge,
  RemoveButton,
  Spinner,
  AddButton,
  Hint,
  ErrorText,
} from './ImageUploader.styles';

// Fotos de actividad: hasta 5, una por petición, con vista previa y botón de
// quitar. `images` es la lista de URLs ya subidas (lo que se guarda en la
// actividad); la primera es la portada.
const ImageUploader = ({ images, onChange, onBusyChange, max = MAX_ACTIVITY_IMAGES }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState([]); // [{ key, preview }]
  const [error, setError] = useState('');
  const mounted = useRef(true);
  const { confirm, dialog } = useConfirm();

  // StrictMode (desarrollo) monta, desmonta y vuelve a montar: hay que volver a marcarlo como montado
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const busy = uploading.length > 0;
  const used = images.length + uploading.length;
  const full = used >= max;

  useEffect(() => {
    if (onBusyChange) onBusyChange(busy);
  }, [busy, onBusyChange]);

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;

    setError('');
    const room = max - images.length;
    const accepted = files.slice(0, room);
    if (files.length > room) {
      setError(`Máximo ${max} fotos por actividad: se omitieron ${files.length - room}.`);
    }

    const queue = accepted.map((file, i) => ({
      file,
      key: `${Date.now()}-${i}`,
      preview: URL.createObjectURL(file),
    }));
    setUploading(queue.map(({ key, preview }) => ({ key, preview })));

    // Una foto por petición, en orden. `current` evita leer `images` viejo.
    let current = images;
    const failures = [];
    for (const item of queue) {
      try {
        const url = await uploadWithResizePrompt(
          uploadActivityImage,
          item.file,
          (name) => confirm(resizeConfirmOptions(name))
        );
        if (url) {
          current = [...current, url];
          if (mounted.current) onChange(current);
        }
      } catch (e) {
        failures.push(`${item.file.name}: ${e.message}`);
      } finally {
        URL.revokeObjectURL(item.preview);
        if (mounted.current) {
          setUploading((prev) => prev.filter((u) => u.key !== item.key));
        }
      }
    }
    if (failures.length && mounted.current) setError(failures.join(' · '));
  };

  const handleRemove = (index) => {
    setError('');
    onChange(images.filter((_, i) => i !== index));
  };

  return (
    <UploaderWrap>
      {used > 0 && (
        <PreviewGrid>
          {images.map((url, index) => (
            <PreviewItem key={url}>
              <img src={url} alt={`Foto ${index + 1}`} />
              {index === 0 && <CoverBadge>Portada</CoverBadge>}
              <RemoveButton
                type="button"
                onClick={() => handleRemove(index)}
                disabled={busy}
                aria-label={`Quitar foto ${index + 1}`}
              >
                ×
              </RemoveButton>
            </PreviewItem>
          ))}
          {uploading.map((item) => (
            <PreviewItem key={item.key} $uploading>
              <img src={item.preview} alt="Subiendo foto" />
              <Spinner>Subiendo…</Spinner>
            </PreviewItem>
          ))}
        </PreviewGrid>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={handleFiles}
      />
      <AddButton
        type="button"
        onClick={() => inputRef.current && inputRef.current.click()}
        disabled={full || busy}
      >
        📷 {images.length === 0 ? 'Agregar fotos' : 'Agregar más fotos'} ({used}/{max})
      </AddButton>
      <Hint>JPEG, PNG o WebP. Hasta {max} fotos; la primera es la portada.</Hint>
      {error && <ErrorText role="alert">{error}</ErrorText>}
      {dialog}
    </UploaderWrap>
  );
};

export default ImageUploader;
